import React, { useEffect, useState } from 'react';
import { BioScoreResult, WorkflowProps } from '@/types/workflow';
import styles from './averageStyles.module.scss';
import {
	RoomData,
	RoomScoreBreakdown,
	SpaceData,
} from '@/components/SpacePlanningPanel/SpacePlanningTypes';
import { AnalysisResult } from '@/services/score/noise';
import {
	BIO_SCORE_GRADIENTS,
	calculateFinalBioScoreForRoom,
	getBioScoreColorLevel,
	calculateBioScoreNoise,
	getRoomBonusFlags,
	getBonusContributions,
	BonusContribution,
} from '@/services/score/bioScore';
import { WeaveBanner } from '@/components/UI/WeaveBanner/WeaveBanner';
import SkeletonLoader from '@/components/UI/SkeletonLoader/SkeletonLoader';
import { normalizePaletteState, PALETTE_WORKFLOW_SECTION } from '@/types/palette';
import { calculateMaterialScoreFromIds, MaterialScoreDetails } from '@/services/score/material';
import { materialService } from '@/services/material.service';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';
import { MaterialBreakdown } from '@/components/SpacePlanningPanel/MaterialBreakdown';
import { BonusBreakdown } from '@/components/SpacePlanningPanel/BonusBreakdown';
import { ViewScoreBreakdown, calculateViewScore } from '@/services/score/view';
import { SunlightScoreBreakdown, calculateSunlightScore } from '@/services/score/sunlight';
import { useViewScoreStore } from '@/store/viewScoreStore';
import { ViewBreakdown } from '@/components/ViewBreakdown/ViewBreakdown';
import { SunlightBreakdown } from '@/components/SunlightBreakdown/SunlightBreakdown';

interface RoomScoreSummary {
	spaceName?: string;
	spaceScore?: number;
	roomName: string;
	level?: number;
	roomId: string;
	quantity: number;
	roomScoreBreakdown?: RoomScoreBreakdown;
	roomScore?: number;
	noiseScore?: number;
	rawNoiseScore?: number;
	materialBreakdown?: MaterialScoreDetails;
	bonusContributions?: BonusContribution[];
	materialSource?: 'explicit' | 'palette_assumptions' | 'palette_baseline';
	viewBreakdown?: ViewScoreBreakdown,
	sunlightBreakdown?: SunlightScoreBreakdown,
}

interface FunctionProxySummary {
	functionKey: string;
	functionName: string;
	paletteName: string;
	proxyScore: number;
}

type SpacesWithRooms = Record<string, SpaceData & { rooms: RoomData[] }>;

const hasExplicitMaterialSelection = (room: RoomData): boolean => {
	return Boolean(room.materials?.wall && room.materials?.floor && room.materials?.ceiling);
};

const AverageBioScore: React.FC<WorkflowProps> = ({
	onDataChange,
	workflowData,
	activeSection,
}) => {
	const spaces = useSpacePlanningStore((state) => state.spaces);
	const horizonContexts = useViewScoreStore((state) => state.horizonContexts);
	const isBeyondHorizonEnabled = useViewScoreStore((state) => state.isBeyondHorizonEnabled);
	const [isLoading, setIsLoading] = useState(false);
	const [bioScoreResult, setBioScoreResult] = useState<BioScoreResult>({
		data: workflowData[activeSection]?.data ?? null,
	});
	const [averageBioScore, setAverageBioScore] = useState<number | null>(null);
	const [roomSummaries, setRoomSummaries] = useState<RoomScoreSummary[]>([]);
	const [functionProxySummaries, setFunctionProxySummaries] = useState<FunctionProxySummary[]>(
		[]
	);
	const activeFunctionsData = workflowData[2]?.data as SpacesWithRooms | undefined;
	const paletteState = normalizePaletteState(workflowData[PALETTE_WORKFLOW_SECTION]?.data);
	const paletteById = Object.fromEntries(
		paletteState.palettes.map((palette) => [palette.id, palette])
	);
	const noiseData = workflowData[3]?.data as AnalysisResult | undefined;
	const rawNoiseScore = Math.round(noiseData?.weightedScore ?? 0);
	const buildingNoiseAdjustment = calculateBioScoreNoise({ noise: rawNoiseScore });
	const recalculationKey = JSON.stringify({
		spaces,
		activeFunctions: activeFunctionsData ?? null,
		noise: workflowData[3]?.data ?? null,
		daylight: workflowData[4]?.data ?? null,
		palettes: workflowData[PALETTE_WORKFLOW_SECTION]?.data ?? null,
	});

	const extractRoomsData = (dataObj: SpacesWithRooms): RoomScoreSummary[] => {
		const result: RoomScoreSummary[] = [];
		const resolveMaterial = (id: string) => materialService.getMaterialById(id);
		Object.keys(dataObj).forEach((key) => {
			const mainObject = dataObj[key];
			const { name, score, rooms } = mainObject;

			rooms.forEach((room) => {
				const levelScopedTypologyAreaKey = room.spaceDescription
					? typeof room.level === 'number'
						? `${key}::L${room.level}::${room.spaceDescription}`
						: `${key}::${room.spaceDescription}`
					: undefined;
				const typologyAreaKey = room.spaceDescription
					? `${key}::${room.spaceDescription}`
					: undefined;
				const areaPaletteId =
					paletteState.stage === 'predesign_programming'
						? undefined
						: (levelScopedTypologyAreaKey
							? paletteState.typologyAssignments[levelScopedTypologyAreaKey]
							: undefined) ||
						(typologyAreaKey
							? paletteState.typologyAssignments[typologyAreaKey]
							: undefined) ||
						(room.spaceDescription
							? paletteState.typologyAssignments[room.spaceDescription]
							: undefined);
				const functionPaletteId = paletteState.functionAssignments[key];
				const assignedPaletteId = areaPaletteId || functionPaletteId;
				const assignedPalette = assignedPaletteId
					? paletteById[assignedPaletteId]
					: undefined;
				const assumptions = assignedPalette?.materialAssumptions;
				const fallbackMaterialBreakdown = calculateMaterialScoreFromIds(
					{
						wallMaterialId: assumptions?.wall,
						floorMaterialId: assumptions?.floor,
						ceilingMaterialId: assumptions?.ceiling,
						wallArea: room.wallAreaTotal ?? 0,
						floorArea: room.ASF ?? 0,
						ceilingArea: room.ASF ?? 0,
					},
					resolveMaterial
				);
				const fallbackMaterialScore =
					fallbackMaterialBreakdown?.score ?? assignedPalette?.baselineScore;

				const shouldApplyFallback =
					!hasExplicitMaterialSelection(room) &&
					(typeof room.scoreBreakdown?.materials !== 'number' ||
						room.scoreBreakdown?.materials <= 0) &&
					typeof fallbackMaterialScore === 'number';

				const hasExplicitMaterials = hasExplicitMaterialSelection(room);
				const materialSource: RoomScoreSummary['materialSource'] = hasExplicitMaterials
					? 'explicit'
					: fallbackMaterialBreakdown
						? 'palette_assumptions'
						: shouldApplyFallback
							? 'palette_baseline'
							: undefined;

				const roomForScoring: RoomData = shouldApplyFallback
					? {
						...room,
						scoreBreakdown: {
							...room.scoreBreakdown,
							materials: fallbackMaterialScore,
						},
					}
					: room;

				let materialBreakdown: MaterialScoreDetails | undefined =
					calculateMaterialScoreFromIds(
						{
							wallMaterialId: roomForScoring.materials?.wall,
							floorMaterialId: roomForScoring.materials?.floor,
							ceilingMaterialId: roomForScoring.materials?.ceiling,
							wallArea: roomForScoring.wallAreaTotal ?? 0,
							floorArea: roomForScoring.ASF ?? 0,
							ceilingArea: roomForScoring.ASF ?? 0,
						},
						resolveMaterial
					);
				if (!materialBreakdown && fallbackMaterialBreakdown) {
					materialBreakdown = fallbackMaterialBreakdown;
				}

				const { displayedScore } = calculateFinalBioScoreForRoom(roomForScoring);

				const viewBreakdown =
					roomForScoring.viewBreakdown ??
					(roomForScoring.view
						? calculateViewScore(
							roomForScoring.view,
							horizonContexts,
							isBeyondHorizonEnabled
						)
						: undefined);

				const sunlightBreakdown = roomForScoring.sunlight
					? calculateSunlightScore(roomForScoring.sunlight)
					: undefined;

				result.push({
					spaceName: name,
					spaceScore: score,
					roomName: roomForScoring.spaceDescription,
					level: roomForScoring.level,
					roomId: String(roomForScoring.spaceId),
					quantity: room.quantity,
					roomScoreBreakdown: roomForScoring.scoreBreakdown,
					roomScore: displayedScore || 0,
					materialBreakdown,
					materialSource,
					bonusContributions: getBonusContributions(getRoomBonusFlags(roomForScoring)),
					viewBreakdown,
					sunlightBreakdown,
				});
			});
		});

		return result;
	};

	const calculateFunctionProxyData = (): FunctionProxySummary[] => {
		if (!activeFunctionsData) return [];

		return Object.keys(activeFunctionsData)
			.map((functionKey) => {
				const functionName = activeFunctionsData[functionKey]?.name || functionKey;
				const paletteId = paletteState.functionAssignments[functionKey];
				if (!paletteId) return null;
				const palette = paletteById[paletteId];
				if (!palette) return null;

				return {
					functionKey,
					functionName,
					paletteName: palette.name,
					proxyScore: palette.baselineScore,
				} satisfies FunctionProxySummary;
			})
			.filter((item): item is FunctionProxySummary => item !== null);
	};

	const calculateAllScoreData = (): RoomScoreSummary[] => {
		// Use only currently active functions (section 2 keys) and enrich with latest function names.
		const spacesData =
			activeFunctionsData && Object.keys(activeFunctionsData).length > 0
				? (Object.fromEntries(
					Object.keys(activeFunctionsData).flatMap((key) => {
						const storedSpace = (spaces as SpacesWithRooms | undefined)?.[key];
						if (!storedSpace) return [];
						return [
							[
								key,
								{
									...storedSpace,
									name:
										activeFunctionsData[key]?.name ||
										storedSpace.name ||
										key,
								},
							],
						];
					})
				) as SpacesWithRooms)
				: undefined;

		if (!spacesData) return [];

		const roomSummaries = extractRoomsData(spacesData);

		return roomSummaries.map((room) => ({
			...room,
			noiseScore: Number(buildingNoiseAdjustment.toFixed(2)),
			rawNoiseScore,
		}));
	};

	const calculateAverageBioScore = () => {
		setIsLoading(true);

		const data = calculateAllScoreData();
		if (!data.length) {
			setRoomSummaries([]);
			const functionProxy = calculateFunctionProxyData();
			setFunctionProxySummaries(functionProxy);

			if (functionProxy.length > 0) {
				const proxyAverage =
					functionProxy.reduce((sum, item) => sum + item.proxyScore, 0) /
					functionProxy.length;
				const finalAverage = Math.min(
					Number((proxyAverage + buildingNoiseAdjustment).toFixed(2)),
					10
				);

				setAverageBioScore(finalAverage);
				setBioScoreResult((prev) => ({
					...prev,
					data: {
						rooms: 0,
						roomAverage: Number(proxyAverage.toFixed(2)),
						noiseAdjustment: Number(buildingNoiseAdjustment.toFixed(2)),
						averageBioScore: finalAverage,
						mode: 'function_proxy',
					},
				}));
			} else {
				setAverageBioScore(null);
				setBioScoreResult((prev) => ({
					...prev,
					data: null,
				}));
			}

			setIsLoading(false);
			return;
		}

		setRoomSummaries(data);
		setFunctionProxySummaries([]);

		// 1. Room-based average
		let roomScoreSum = 0;
		data.forEach((room) => {
			roomScoreSum += room.roomScore ?? 0;
		});

		const roomAverage = data.length ? roomScoreSum / data.length : 0;

		// 2. Global noise adjustment
		const noiseAdjustment = data[0]?.noiseScore ?? 0;

		const unclampedAverage = Number((roomAverage + noiseAdjustment).toFixed(2));
		const finalAverage = Math.min(unclampedAverage, 10);
		setAverageBioScore(finalAverage);

		// Forward assessment data
		setBioScoreResult((prev) => ({
			...prev,
			data: {
				rooms: data.length,
				roomAverage: Number(roomAverage.toFixed(2)),
				noiseAdjustment: Number(noiseAdjustment.toFixed(2)),
				averageBioScore: finalAverage,
			},
		}));

		setIsLoading(false);
	};

	useEffect(() => {
		calculateAverageBioScore();
		// Recalculate whenever analysis/material assumptions change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recalculationKey]);

	useEffect(() => {
		onDataChange({
			data: bioScoreResult.data,
		});
	}, [bioScoreResult, onDataChange]);

	if (isLoading) return <SkeletonLoader title="Loading BioScore data..." loading={isLoading} />;

	return (
		<div className={styles.averageBioScoreContainer}>
			<div className={styles.instructions}>
				<div className={styles.instructionsHeader}>
					<h2 className={styles.instructionsTitle}>BioScore Results</h2>
				</div>
				<p className={styles.instructionsText}>
					Results reflect available project data: function-level assumptions in early
					stages and room-level analysis when defined.
				</p>
			</div>
			<div>
				<div className={styles.banner}>
					<div className={styles.bannerTitle}>Building Summary</div>
					<div className={styles.bannerValue}>
						Average BioScore: <strong>{averageBioScore ?? '–'}</strong>
					</div>
					{bioScoreResult.data && (
						<div>
							({bioScoreResult.data.roomAverage}{' '}
							{bioScoreResult.data.mode === 'function_proxy'
								? 'function proxy average'
								: 'room average'}{' '}
							+ {bioScoreResult.data.noiseAdjustment} environmental noise adjustment)
						</div>
					)}
				</div>
				{functionProxySummaries.length > 0 && roomSummaries.length === 0 && (
					<div className={styles.proxyNotice}>
						Function-level proxy scores are shown (no rooms defined yet).
					</div>
				)}
				<div className={styles.tableContainer}>
					<table className={styles.table}>
						<tbody>
							<tr className={styles.sectionHeader}>
								<td>Room</td>
								<td>Count</td>
								<td>Material</td>
								<td>View</td>
								<td>Daylight</td>
								<td>Bonus</td>
								<td>Total</td>
							</tr>

							{functionProxySummaries.length > 0 && roomSummaries.length === 0
								? functionProxySummaries.map((item) => {
									return (
										<tr key={`${item.functionKey}-${item.paletteName}`}>
											<td>
												<strong>{item.functionName}</strong> (Predesign
												- {item.paletteName})
											</td>
											<td>-</td>
											<td>{item.proxyScore}</td>
											<td>-</td>
											<td>-</td>
											<td>-</td>
											<td>-</td>
										</tr>
									);
								})
								: roomSummaries.map((room, index) => {
									const colorLevel = getBioScoreColorLevel(
										room.roomScore ?? 0
									);
									const roomLabel =
										typeof room.level === 'number'
											? `Level ${room.level} - ${room.roomName}`
											: room.roomName;
									return (
										<tr key={`${room.spaceName}-${room.roomName}-${index}`}>
											<td>
												<strong>{room.spaceName}</strong> –{' '}
												{roomLabel}
											</td>
											<td>{room.quantity}</td>
											<td>
												{typeof room.roomScoreBreakdown?.materials ===
													'number' ? (
													// eslint-disable-next-line react/jsx-indent
													<>
														<span
															id={`bio-material-${room.roomId}-${index}`}
															className={
																room.materialSource ===
																	'palette_baseline'
																	? styles.fallbackMaterialValue
																	: styles.breakdownHoverTarget
															}
															title={
																room.materialSource ===
																	'palette_baseline'
																	? 'Using preset palette baseline score (no room material selection yet).'
																	: undefined
															}
														>
															{room.roomScoreBreakdown.materials}
														</span>
														{room.materialBreakdown ? (
															<MaterialBreakdown
																id={`bio-material-${room.roomId}-${index}`}
																scoreData={
																	room.materialBreakdown
																}
															/>
														) : null}
													</>
												) : (
													'-'
												)}
											</td>
											<td>
												{typeof room.roomScoreBreakdown?.view ===
													'number' ? (
													// eslint-disable-next-line react/jsx-indent
													<>
														<span
															id={`bio-view-${room.roomId}-${index}`}
															className={
																room.materialSource ===
																	'palette_baseline'
																	? styles.fallbackMaterialValue
																	: styles.breakdownHoverTarget
															}
															title={
																room.materialSource ===
																	'palette_baseline'
																	? 'Using preset palette baseline score (no room material selection yet).'
																	: undefined
															}
														>
															{room.roomScoreBreakdown.view}
														</span>
														{room.viewBreakdown ? (
															<ViewBreakdown
																id={`bio-view-${room.roomId}-${index}`}
																scoreData={
																	room.viewBreakdown
																}
															/>
														) : null}
													</>
												) : (
													'-'
												)}
											</td>
											<td>
												{typeof room.roomScoreBreakdown?.sunlight ===
													'number' ? (
													// eslint-disable-next-line react/jsx-indent
													<>
														<span
															id={`bio-sunlight-${room.roomId}-${index}`}
															className={
																room.materialSource ===
																	'palette_baseline'
																	? styles.fallbackMaterialValue
																	: styles.breakdownHoverTarget
															}
															title={
																room.materialSource ===
																	'palette_baseline'
																	? 'Using preset palette baseline score (no room material selection yet).'
																	: undefined
															}
														>
															{room.roomScoreBreakdown.sunlight}
														</span>
														{room.sunlightBreakdown ? (
															<SunlightBreakdown
																id={`bio-sunlight-${room.roomId}-${index}`}
																scoreData={
																	room.sunlightBreakdown
																}
															/>
														) : null}
													</>
												) : (
													'-'
												)}
											</td>
											<td>
												{typeof room.roomScoreBreakdown?.bonus ===
													'number' ? (
													// eslint-disable-next-line react/jsx-indent
													<>
														<span
															id={`bio-bonus-${room.roomId}-${index}`}
															className={
																styles.breakdownHoverTarget
															}
														>
															{room.roomScoreBreakdown.bonus}
														</span>
														<BonusBreakdown
															id={`bio-bonus-${room.roomId}-${index}`}
															score={
																room.roomScoreBreakdown.bonus
															}
															contributions={
																room.bonusContributions ?? []
															}
														/>
													</>
												) : (
													'-'
												)}
											</td>
											<td>
												<span
													className={styles.totalBioScoreValue}
													style={{
														background:
															BIO_SCORE_GRADIENTS[colorLevel],
													}}
												>
													{room.roomScore}
												</span>
											</td>
										</tr>
									);
								})}
						</tbody>
					</table>
				</div>
			</div>
			<div>
				<h3>Environmental Context</h3>
				<WeaveBanner
					variant="forma-neutral"
					text={
						<>
							<p>
								<strong>Noise level (location-based):</strong> {rawNoiseScore} / 10
							</p>
							<p>
								This noise score reflects the surrounding environmental conditions
								and is applied uniformly to the entire building. It is included as
								an adjustment in the Average BioScore with a BioScore impact of{' '}
								<strong>{`+${Number(buildingNoiseAdjustment.toFixed(2))}`}.</strong>
							</p>
						</>
					}
				/>
			</div>
		</div>
	);
};

export default AverageBioScore;
